import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { TICKET_SORT_OPTIONS } from "constants/ticket";
import { useUpdateQuery } from "hooks/useQuery";
import SortFunnelIcon from "static/images/SortFunnel.svg";

type TicketFilterProps = {
  onSortTabSelect: (val: string) => void;
  selectedSort: string;
  resolvedStatus: boolean;
  selectedTicketETD?: string | null;
};

export const TicketFilter = ({
  onSortTabSelect,
  selectedSort,
  resolvedStatus,
  selectedTicketETD,
}: TicketFilterProps) => {
  const isUnsetETDActive = Boolean(selectedTicketETD?.includes("UNSET"));
  const isDueTodayActive = Boolean(selectedTicketETD?.includes("DUE_TODAY"));
  const isOverdueActive = Boolean(selectedTicketETD?.includes("OVERDUE"));
  const { update } = useUpdateQuery();

  const handleETDClick = (type: string, isActive: boolean) => {
    if (isActive) {
      const updatedValue = selectedTicketETD
        ?.split(",")
        .filter((filter) => filter !== type)
        .join(",");
      update({ query: "ticket_etd", value: updatedValue || null });
    } else {
      const newValue = selectedTicketETD
        ? `${selectedTicketETD},${type}`
        : type;
      update({ query: "ticket_etd", value: newValue });
    }
  };

  const selectedSortOption = Object.values(TICKET_SORT_OPTIONS).find(
    (option) => option.value === selectedSort
  );

  return (
    <div className="t-flex t-items-center t-gap-2 sm:t-flex-wrap">
      <div>
        <Dropdown.Root>
          <Dropdown.Trigger className="all:unset ">
            <Button size="xsmall">
              <div className="t-flex t-gap-1.5">
                <img src={SortFunnelIcon} alt="SortFunnelIcon" />
                <div className="t-text-text-60">
                  {selectedSortOption?.group}:&nbsp;
                  {selectedSortOption?.label}
                </div>
              </div>
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              sideOffset={8}
              align="start"
              className="t-p-3 t-w-full"
            >
              <Radio.Root
                value={selectedSort}
                onValueChange={(value) => onSortTabSelect(value)}
              >
                <Radio.Content className="t-gap-4 t-flex t-flex-col">
                  <div className="t-flex t-flex-col t-gap-2">
                    <div className="t-text-body-sm t-text-text-30">
                      {TICKET_SORT_OPTIONS.TICKET_TYPE_NEAREST_ETD_SORT.group}
                    </div>
                    <Radio.Item
                      value={
                        TICKET_SORT_OPTIONS.TICKET_TYPE_NEAREST_ETD_SORT.value
                      }
                    >
                      {TICKET_SORT_OPTIONS.TICKET_TYPE_NEAREST_ETD_SORT.label}
                    </Radio.Item>
                    <Radio.Item
                      value={
                        TICKET_SORT_OPTIONS.TICKET_TYPE_FARTHEST_ETD_SORT.value
                      }
                    >
                      {TICKET_SORT_OPTIONS.TICKET_TYPE_FARTHEST_ETD_SORT.label}
                    </Radio.Item>
                  </div>
                  <div className="t-flex t-flex-col t-gap-2">
                    <div className="t-text-body-sm t-text-text-30">
                      {TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.group}
                    </div>
                    <Radio.Item
                      value={TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.value}
                    >
                      {TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.label}
                    </Radio.Item>
                    <Radio.Item
                      value={TICKET_SORT_OPTIONS.TICKET_TYPE_OLDEST_SORT.value}
                    >
                      {TICKET_SORT_OPTIONS.TICKET_TYPE_OLDEST_SORT.label}
                    </Radio.Item>
                  </div>
                  {resolvedStatus && (
                    <div className="t-flex t-flex-col t-gap-2">
                      <div className="t-text-body-sm t-text-text-30">
                        {
                          TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT
                            .group
                        }
                      </div>
                      <Radio.Item
                        value={
                          TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT
                            .value
                        }
                      >
                        {
                          TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT
                            .label
                        }
                      </Radio.Item>
                      <Radio.Item
                        value={
                          TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_OLDEST_SORT
                            .value
                        }
                      >
                        {
                          TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_OLDEST_SORT
                            .label
                        }
                      </Radio.Item>
                    </div>
                  )}
                </Radio.Content>
              </Radio.Root>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>

      <Chip
        onClick={() => handleETDClick("UNSET", isUnsetETDActive)}
        isActive={isUnsetETDActive}
        isFixedFilter
        onRemove={() => handleETDClick("UNSET", isUnsetETDActive)}
      >
        No ETD
      </Chip>
      <Chip
        isFixedFilter
        onClick={() => handleETDClick("OVERDUE", isOverdueActive)}
        isActive={isOverdueActive}
        onRemove={() => handleETDClick("OVERDUE", isOverdueActive)}
      >
        Overdue
      </Chip>

      <Chip
        isFixedFilter
        isActive={isDueTodayActive}
        onRemove={() => handleETDClick("DUE_TODAY", isDueTodayActive)}
        onClick={() => handleETDClick("DUE_TODAY", isDueTodayActive)}
      >
        Due today
      </Chip>
    </div>
  );
};
